import { Fragment, useCallback, useContext, useEffect, useState, useRef } from "react";
import CustomText from "../../components/atomic/CustomText";
import DataTable from "../../components/global/DataTable";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
} from "@tanstack/react-table";
import useQueryParams from "../../services/utils/custom hooks/QueryHelper";
import usePatientsList from "../../services/utils/custom hooks/Patients/usePatientsList";
import CustomTextInput from "../../components/global/CustomTextInput";
import PatientsActionCell from "../../components/Tables/PatientsTable/PatientActionsCell";
import AdminContentLayout from "../../layouts/AdminContentLayout";
import ErrorBoundary from "../../components/global/ErrorBoundary";
import { PopUpContext } from "../../services/context/popupContext";
import { useOktaAuth } from "@okta/okta-react";
import OrganizationAndClinicianSelection from "../../components/popups/organizationAndClinicianSelectionPopup";
import CreatePatientPopup from "../../components/popups/createPatientPopup";
import { ApplicationState } from "../../services/context/ApplicationStateContext";
import TableLoadingIndicator from "../../components/global/LoadingIndicators/TableLoadingIndicator";
import ContextFilters from "../../components/global/ContextFiltersForTables";
import useProfile from "../../services/utils/custom hooks/useProfile";
import InfoSliderDispatcher from "../../components/molecular/InfoSliderDispatcherButton";
import dataConstants from "../../constants/dataConstants";
import useFeatureFlags from "../../services/utils/custom hooks/Feature Flagging/useFeatureFlags";
import { useHistory, useLocation } from "react-router-dom";
import Button from "../../components/organism/Button";
import CalendarIcon from "../../assets/svg/Calendar";
import CustomCalendar from "../../components/global/CustomCalendar/CustomCalendar";
import moment from "moment";
import { useDebouncedDateDetection } from "../../services/utils/custom hooks/useDebouncedDateDetection";

function PatientsList(): JSX.Element {
  const oktaAuth = useOktaAuth();
  const {
    state: { selectedOrganization },
    dispatch,
  } = useContext(ApplicationState);
  const { getActionsForSection } = useFeatureFlags();
  const profile: any = useProfile();
  const [params, setParams] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [inputText, setInputText] = useState<string>("");
  const [patientDob, setPatientDob] = useState<string>("");
  const [showDateInput, setShowDateInput] = useState<boolean>(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const [pageQueries, setPageQueries] = useState<string[]>([""]);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [sorting, setSorting] = useState<SortingState>([]);
  // Use searchText for query parameter, patientDob as separate parameter
  const { data, isLoading } = usePatientsList(
    searchText,
    pageQueries[pageIndex],
    sorting[0] &&
    `${sorting?.[0]?.desc
      ? `-${sorting[0].id?.toLowerCase()}`
      : sorting[0]?.id?.toLowerCase()
    }`,
    patientDob
  );
  const roles = profile?.user?.roles ?? [];
  const query = useQueryParams();
  const location = useLocation();
  const { visible, setVisible, setPopUp, setPopUpTitle, setOnlineOrder } =
    useContext(PopUpContext);
  const { dispatch: applicationStateDispatcher } = useContext(ApplicationState);
  const [columns, setColumns] = useState<ColumnDef<any, unknown>[]>([
    { accessorKey: "id", header: "ID" },
    { accessorKey: "name", header: "Patient", meta: { obsecure: true } },
    // { accessorKey: "firstName", header: "First Name" },
    // { accessorKey: "lastName", header: "Last Name" },
    { accessorKey: "birthDate", header: "DOB", meta: { obsecure: true } },
    {
      accessorKey: "ssn",
      header: "SSN",
      enableSorting: false,
      meta: { obsecure: true },
    },
    {
      accessorKey: "mrn",
      header: "MRN",
      enableSorting: false,
      meta: { obsecure: true },
    },
    {
      accessorKey: "phone",
      header: "Phone",
      enableSorting: false,
      meta: { obsecure: true },
    },
    {
      accessorKey: "city",
      header: "City",
      enableSorting: false,
      meta: { obsecure: true },
    },
    {
      accessorKey: "state",
      header: "State",
      enableSorting: false,
      meta: { obsecure: true },
    },
    {
      accessorKey: "Actions",
      header: "Actions",
      cell: PatientsActionCell,
      enableSorting: false,
    },
  ]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    columns
      .map((column: any) => ({
        id: column.accessorKey,
        value: query.get(column.accessorKey),
      }))
      .filter((data) => !!data.value)
  );
  const [showClear, setShowClear] = useState(false);
  
  // Use custom hook for debounced date detection
  const { debouncedDateDetection, debounceSearch } = useDebouncedDateDetection({
    patientDob,
    setPatientDob,
    setInputText,
    setSearchText,
  });
  
  const [orders, setOrders] = useState<any>([]);
  const [firstLoading, setFirstLoading] = useState(true);
  const history = useHistory();
  const isPhysicianOnly = roles.length === 1 && roles.includes("Physician");

  function handleSearch(value: string) {
    // Update input text immediately for display
    setInputText(value);
    
    // Debounce the date detection - only check after user stops typing
    debouncedDateDetection(value);
  }

  function handleCalendarClick(e: React.MouseEvent) {
    e.stopPropagation();
    setShowDateInput(!showDateInput);
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node) &&
        inputContainerRef.current &&
        !inputContainerRef.current.contains(event.target as Node)
      ) {
        setShowDateInput(false);
      }
    }

    if (showDateInput) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDateInput]);

  function createPatient() {
    setVisible(!visible);
    setPopUpTitle("Create a Patient");
    setPopUp(<CreatePatientPopup oktaAuth={oktaAuth} />);
  }

  function handleNextPage() {
    let temp = pageQueries;
    if (data?.nextPage) temp.push(data?.nextPage);
    setPageQueries(temp);
    setPageIndex((prevState) => prevState + 1);
  }

  function handlePreviousPage() {
    let temp = pageQueries;
    temp.pop();
    setPageQueries(temp);
    if (pageIndex > 0) setPageIndex(pageIndex - 1);
  }
  function handleClearFilters() {
    setColumnFilters([]);
    setSorting([]);
    setPatientDob("");
    handleSearch("");
    if (
      profile?.user?.roles?.includes?.("Physician") &&
      profile?.user?.roles?.length === 1
    ) {
      applicationStateDispatcher({ type: "SET_ORGANIZATION", payload: null });
      applicationStateDispatcher({
        type: "SET_ORGANIZATION_DATA",
        payload: null,
      });
    } else applicationStateDispatcher({ type: "RESET" });
  }

  useEffect(() => {
    setPageIndex(0);
  }, [selectedOrganization]);

  // Initialize search from URL query parameter
  useEffect(() => {
    const urlQuery = query.get("query");
    const urlPatientDOB = query.get("patientDOB");
    
    if (urlQuery && !inputText) {
      setInputText(urlQuery);
      setSearchText(urlQuery);
    }
    
    if (urlPatientDOB && !patientDob) {
      setPatientDob(urlPatientDOB);
      // Display the date in MM-DD-YYYY format (consistent with date detection behavior)
      if (!inputText) {
        setInputText(urlPatientDOB);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (!isLoading) {
      setOrders(data?.data ?? []);
      if (firstLoading && !!data?.data) setFirstLoading(false);
    }
    if (inputText.length > 0 || selectedOrganization) {
      setShowClear(true);
    } else {
      setShowClear(false);
    }
  }, [isLoading, data]);

  function handleRowClick(row: any) {
    history.push(`/access/patients/${row?.original?.id}`);
  }

  return (
    <AdminContentLayout
      pageTitle="Patients | CSTL"
      subHeaderTitle="Patients"
      // showButtonInSubheaderForMobile
      // hideSubHeaderSelectors
      customButtons={[
        {
          buttonTheme: "primary",
          key: dataConstants.SectionActionKeys.CREATE_NEW_PATIENT,
          textInformation: {
            text: "Create new patient",
            fontWeight: "font-semibold",
            fontSize: "text-sm",
          },
          onClick: () => {
            dispatch({ type: "HIDE_INFO_SLIDER", payload: undefined });
            setVisible(!visible);
            setPopUpTitle(`Select an organization`);
            setPopUp(
              <OrganizationAndClinicianSelection
                handleNext={createPatient}
                createPatientFlow
              />
            );
          },
          className: "mr-6",
          // disabled: canCreateOrder ? false : true,
        },
      ].filter(
        (button) =>
          getActionsForSection(
            dataConstants.FeatureFlagSectionKeys.PATIENTS
          ).find((action) => action.key === button.key)?.isActive
      )}
    >
      <ErrorBoundary>
        <div className="md:p-5 py-5 px-3 overflow-auto">
          <div className="flex justify-start items-center">
            <CustomText
              text="Patients List"
              fontWeight="font-semibold"
              fontSize="text-sm"
              className="my-2"
            />
            <InfoSliderDispatcher
              infoQuery={dataConstants.infoSliderQuery.patients}
            />
          </div>
          <div className="relative md:max-w-[40%]">
            <div ref={inputContainerRef} className="relative">
              <CustomTextInput
                placeholder={'Search using Patient\'s Name, SSN, MRN or DOB'}
                roundedInput
                onChange={(e) => handleSearch(e.target.value)}
                value={inputText}
                className="bg-white pr-10"
              />
              <button
                type="button"
                onClick={handleCalendarClick}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 z-10 cursor-pointer"
              >
                <CalendarIcon className="w-4 h-4" stroke={showDateInput ? "#18B5B7" : "#667085"} />
              </button>
            </div>
            {showDateInput && (
              <div
                ref={calendarRef}
                className="absolute top-full right-0 mt-2 bg-white border border-dropDownBorder rounded-lg shadow-lg z-[10001]"
              >
                <CustomCalendar
                  onChange={(date) => {
                    const formattedDate = moment(date).format("MM-DD-YYYY");
                    setSearchText("");
                    setInputText(formattedDate);
                    setPatientDob(formattedDate);
                    setShowDateInput(false);
                  }}
                />
              </div>
            )}
          </div>

          {firstLoading && orders.length === 0 ? (
            <TableLoadingIndicator />
          ) : (
            <Fragment>
              <div className="flex flex-wrap items-center">
                <ContextFilters hidePhysicianFilter />

                {showClear && (
                  <Button
                    buttonTheme="transparent"
                    textInformation={{
                      text: "Clear filters",
                      fontWeight: "font-medium",
                    }}
                    className="!text-primary my-3 ml-2"
                    onClick={
                      handleClearFilters
                        ? () => handleClearFilters()
                        : () => null
                    }
                  />
                )}
              </div>

              <DataTable
                columns={columns}
                setParams={setParams}
                columnFilters={columnFilters}
                tableContainerClass="max-h-[65vh] min-h-[20vh] overflow-auto border relative"
                pagination={{
                  pageIndex: pageIndex + 1,
                  pageSize: 10,
                  pageCount: (data?.total ?? 1) / 10,
                  onNextPage: handleNextPage,
                  onPreviousPage: handlePreviousPage,
                  totalItems: data?.total ?? 0,
                  setPage: () => { },
                  hideRecordsPerPage: true,
                  setPageSize: () => { },
                }}
                enableClientSideFiltering={false}
                enableClientSideSorting={false}
                isFetching={isLoading}
                setColumnFilters={setColumnFilters}
                columnVisibility={{
                  id: false,
                  imageUrl: false,
                }}
                // data={data?.data ?? []}
                data={orders}
                handleRowClick={handleRowClick}
                manualSorting={{
                  sorting,
                  setSorting,
                }}
              // rowClickable={true}
              />
            </Fragment>
          )}
        </div>
      </ErrorBoundary>
    </AdminContentLayout>
  );
}

export default PatientsList;
